import { useRender } from "@base-ui/react/use-render";
import type { ComponentProps, ReactNode, Ref } from "react";
import { cn } from "@/shared/lib/utils";
import { warnOnShadowLevelDrift } from "../surface-classes";
import { SurfaceProvider, useSurface } from "../surface-context";
import {
	clampSurfaceLevel,
	SURFACE_BG_CLASSES,
	SURFACE_SHADOW_CLASSES,
	type SurfaceLevel,
} from "../surface-level";

export type ElevatedProps = Omit<ComponentProps<"div">, "ref"> & {
	/**
	 * How many levels to lift above the current substrate. Defaults to 1.
	 * The resolved level (substrate + offset, clamped to 8) is the level
	 * applied to this surface AND re-provided to descendants.
	 */
	offset?: number;
	/**
	 * Pin the surface to an absolute level instead of offsetting from the
	 * substrate. Useful for components that always sit at a fixed depth
	 * regardless of where they're rendered (toasts, tooltips). Takes
	 * precedence over `offset`.
	 */
	level?: SurfaceLevel;
	/**
	 * Pin the SHADOW recipe to a fixed level while the background floats
	 * with the substrate (Fluid Functionalism: "the shadow doesn't change,
	 * so a popover still reads as a popover three layers down"). Defaults
	 * to the resolved background level (the spec's `shadowLevel ?? level`).
	 * Named roles pass `SURFACE_IDENTITY_SHADOWS.<role>` from surface-roles.
	 */
	shadowLevel?: SurfaceLevel;
	/**
	 * When true, skip the background utility — only the shadow recipe is
	 * applied. Use for surfaces that want elevation cues but keep a custom
	 * background (e.g. a glassmorphic or branded panel).
	 */
	shadowOnly?: boolean;
	/**
	 * When true, skip the shadow utility — only the background utility is
	 * applied. Use for nested cards inside an already-elevated container
	 * that want background contrast without restacking shadows.
	 */
	bgOnly?: boolean;
	/**
	 * Apply the matching surface foreground color. Defaults to false so
	 * surfaces inherit text color from their parent (preserves existing
	 * `text-card-foreground` / `text-popover-foreground` overrides).
	 */
	withForeground?: boolean;
	/**
	 * When true, render a subtle diagonal warm-gradient overlay on top of
	 * the solid surface (transparent → muted/20 in the bottom-right corner).
	 * Brings back the "frosted card" feel of the previous
	 * `bg-linear-to-br from-background via-background to-muted/20` pattern
	 * while keeping the surface system as the authoritative base color.
	 * The overlay sits BEHIND children so consumer-supplied decorative
	 * overlays (colored accent gradients) still render on top. Requires the
	 * element to be `position: relative` (which `relative overflow-hidden`
	 * on the consumer's className typically provides).
	 */
	gradient?: boolean;
	/**
	 * Opt-in dark-mode rim: a single 1px inset hairline ring + top
	 * highlight ("lit from above") painted on an ::after layer ABOVE the
	 * content, so opaque children at the surface edges (sticky headers)
	 * can't cover it. Light mode is a no-op — the drop shadow does the
	 * lifting there. See the `data-surface-rim` rules in surfaces.css.
	 * Reserve for floating overlays; never on in-flow cards by default.
	 */
	rim?: boolean;
	/**
	 * Render-prop escape hatch — same shape as Base UI's `useRender`. When
	 * supplied the elevation classes are merged into the rendered element
	 * instead of a wrapping <div>.
	 */
	render?: ComponentProps<typeof useRender>["render"];
	ref?: Ref<HTMLElement>;
	children?: ReactNode;
};

/**
 * Context-aware elevation primitive (Fluid Functionalism).
 *
 * Reads the substrate level from `<SurfaceProvider>` (default 1), lifts by
 * `offset` (default 1), and re-provides the new level so descendants stack
 * relative to THIS surface — not the page.
 *
 * Example: a card inside a dialog inside a popover inside the page lands at
 * 1 → +2 popover (3) → +2 dialog (5) → +1 card (6). Each surface picks the
 * shadow/background recipe appropriate to its depth automatically.
 */
export function Elevated({
	className,
	offset = 1,
	level,
	shadowLevel,
	shadowOnly = false,
	bgOnly = false,
	withForeground = false,
	gradient = false,
	rim = false,
	render,
	children,
	ref,
	...rest
}: ElevatedProps) {
	const substrate = useSurface();
	const resolved: SurfaceLevel =
		level === undefined ? clampSurfaceLevel(substrate + offset) : level;
	warnOnShadowLevelDrift(resolved, shadowLevel);

	const surfaceClass = cn(
		!shadowOnly && SURFACE_BG_CLASSES[resolved],
		!bgOnly && SURFACE_SHADOW_CLASSES[shadowLevel ?? resolved],
		withForeground && "text-surface-foreground",
		className,
	);

	const wrappedChildren = (
		<SurfaceProvider value={resolved}>
			{gradient ? (
				<span
					aria-hidden="true"
					className="pointer-events-none absolute inset-0 bg-linear-to-br from-transparent via-transparent to-muted/20"
					data-slot="elevated-gradient"
				/>
			) : null}
			{children}
		</SurfaceProvider>
	);

	return useRender({
		defaultTagName: "div",
		render,
		props: {
			"data-slot": "elevated",
			"data-surface-level": String(resolved),
			...(rim ? { "data-surface-rim": "all" } : {}),
			className: surfaceClass,
			ref,
			...rest,
			children: wrappedChildren,
		} as Record<string, unknown>,
	});
}
